import type { ManualTool } from '@/types';

/**
 * Keyboard shortcut definitions for the application
 * Centralized here for easy reference and modification
 */

export interface ShortcutDefinition {
  /** Unique identifier for the shortcut */
  id: string;
  /** The key(s) that trigger this shortcut */
  key: string;
  /** Display-friendly key label */
  keyLabel: string;
  /** Human-readable description */
  description: string;
  /** Category for grouping in shortcuts panel */
  category: 'tools' | 'edit' | 'view' | 'file' | 'help';
  /** Whether Ctrl/Cmd modifier is required */
  ctrl?: boolean;
  /** Whether Shift modifier is required */
  shift?: boolean;
  /** Whether Alt modifier is required */
  alt?: boolean;
  /** Associated tool (for tool shortcuts) */
  tool?: ManualTool;
  /** Action identifier for non-tool shortcuts */
  action?: string;
}

/**
 * Tool shortcuts - single key presses to switch tools
 */
export const toolShortcuts: ShortcutDefinition[] = [
  {
    id: 'tool-select',
    key: 'v',
    keyLabel: 'V',
    description: 'Select Tool',
    category: 'tools',
    tool: 'select',
  },
  {
    id: 'tool-pan',
    key: 'h',
    keyLabel: 'H',
    description: 'Pan/Hand Tool',
    category: 'tools',
    tool: 'pan',
  },
  {
    id: 'tool-move',
    key: 'm',
    keyLabel: 'M',
    description: 'Move Tool',
    category: 'tools',
    tool: 'move',
  },
  {
    id: 'tool-text',
    key: 't',
    keyLabel: 'T',
    description: 'Text Tool',
    category: 'tools',
    tool: 'text',
  },
  {
    id: 'tool-pen',
    key: 'p',
    keyLabel: 'P',
    description: 'Pen/Draw Tool',
    category: 'tools',
    tool: 'pen',
  },
  {
    id: 'tool-eraser',
    key: 'e',
    keyLabel: 'E',
    description: 'Eraser Tool',
    category: 'tools',
    tool: 'eraser',
  },
  {
    id: 'tool-crop',
    key: 'c',
    keyLabel: 'C',
    description: 'Crop Tool',
    category: 'tools',
    tool: 'crop',
  },
  {
    id: 'tool-rotate',
    key: 'r',
    keyLabel: 'R',
    description: 'Rotate Tool',
    category: 'tools',
    tool: 'rotate',
  },
];

/**
 * Edit shortcuts - actions that modify content
 */
export const editShortcuts: ShortcutDefinition[] = [
  {
    id: 'edit-undo',
    key: 'z',
    keyLabel: 'Ctrl+Z',
    description: 'Undo',
    category: 'edit',
    ctrl: true,
    action: 'undo',
  },
  {
    id: 'edit-redo',
    key: 'y',
    keyLabel: 'Ctrl+Y',
    description: 'Redo',
    category: 'edit',
    ctrl: true,
    action: 'redo',
  },
  {
    id: 'edit-redo-alt',
    key: 'z',
    keyLabel: 'Ctrl+Shift+Z',
    description: 'Redo (alternate)',
    category: 'edit',
    ctrl: true,
    shift: true,
    action: 'redo',
  },
  {
    id: 'edit-delete',
    key: 'Delete',
    keyLabel: 'Delete',
    description: 'Delete selected layer',
    category: 'edit',
    action: 'delete',
  },
  {
    id: 'edit-delete-alt',
    key: 'Backspace',
    keyLabel: 'Backspace',
    description: 'Delete selected layer',
    category: 'edit',
    action: 'delete',
  },
  {
    id: 'edit-duplicate',
    key: 'd',
    keyLabel: 'Ctrl+D',
    description: 'Duplicate layer',
    category: 'edit',
    ctrl: true,
    action: 'duplicate',
  },
  {
    id: 'edit-deselect',
    key: 'Escape',
    keyLabel: 'Esc',
    description: 'Deselect / Cancel',
    category: 'edit',
    action: 'deselect',
  },
  {
    id: 'edit-select-all',
    key: 'a',
    keyLabel: 'Ctrl+A',
    description: 'Select all layers',
    category: 'edit',
    ctrl: true,
    action: 'select-all',
  },
];

/**
 * View shortcuts - zoom, pan, grid
 */
export const viewShortcuts: ShortcutDefinition[] = [
  {
    id: 'view-zoom-in',
    key: '=',
    keyLabel: '+',
    description: 'Zoom in',
    category: 'view',
    action: 'zoom-in',
  },
  {
    id: 'view-zoom-in-alt',
    key: '+',
    keyLabel: '+',
    description: 'Zoom in',
    category: 'view',
    action: 'zoom-in',
  },
  {
    id: 'view-zoom-out',
    key: '-',
    keyLabel: '-',
    description: 'Zoom out',
    category: 'view',
    action: 'zoom-out',
  },
  {
    id: 'view-reset',
    key: '0',
    keyLabel: '0',
    description: 'Reset view (100%)',
    category: 'view',
    action: 'reset-view',
  },
  {
    id: 'view-grid',
    key: 'g',
    keyLabel: 'Ctrl+G',
    description: 'Toggle grid',
    category: 'view',
    ctrl: true,
    action: 'toggle-grid',
  },
  {
    id: 'view-flip-side',
    key: 'f',
    keyLabel: 'F',
    description: 'Flip to front/back side',
    category: 'view',
    action: 'flip-side',
  },
  {
    id: 'view-mouse-zoom',
    key: 'scroll',
    keyLabel: 'Ctrl+Scroll',
    description: 'Zoom in/out (desktop)',
    category: 'view',
    ctrl: true,
    action: 'mouse-zoom',
  },
  {
    id: 'view-mouse-pan',
    key: 'right-click-drag',
    keyLabel: 'Right-click+Drag',
    description: 'Pan canvas (desktop)',
    category: 'view',
    action: 'mouse-pan',
  },
];

/**
 * File shortcuts - save, export
 */
export const fileShortcuts: ShortcutDefinition[] = [
  {
    id: 'file-save',
    key: 's',
    keyLabel: 'Ctrl+S',
    description: 'Save project',
    category: 'file',
    ctrl: true,
    action: 'save',
  },
  {
    id: 'file-export',
    key: 'e',
    keyLabel: 'Ctrl+E',
    description: 'Export PNG',
    category: 'file',
    ctrl: true,
    action: 'export',
  },
];

/**
 * Help shortcuts
 */
export const helpShortcuts: ShortcutDefinition[] = [
  {
    id: 'help-shortcuts',
    key: '?',
    keyLabel: '?',
    description: 'Show keyboard shortcuts',
    category: 'help',
    action: 'show-shortcuts',
  },
  {
    id: 'help-debug-panel',
    key: 'p',
    keyLabel: 'Ctrl+Shift+P',
    description: 'Toggle Prompt Debug Panel (Dev Mode)',
    category: 'help',
    ctrl: true,
    shift: true,
    action: 'show-debug-panel',
  },
];

/**
 * All shortcuts combined
 */
export const allShortcuts: ShortcutDefinition[] = [
  ...toolShortcuts,
  ...editShortcuts,
  ...viewShortcuts,
  ...fileShortcuts,
  ...helpShortcuts,
];

/**
 * Get shortcut by tool type
 */
export function getShortcutForTool(tool: ManualTool): ShortcutDefinition | undefined {
  return toolShortcuts.find((s) => s.tool === tool);
}

/**
 * Get shortcut key label for a tool
 */
export function getShortcutLabel(tool: ManualTool): string | undefined {
  return getShortcutForTool(tool)?.keyLabel;
}

/**
 * Get shortcuts grouped by category
 */
export function getShortcutsByCategory(): Record<string, ShortcutDefinition[]> {
  return {
    tools: toolShortcuts,
    edit: editShortcuts.filter((s) => s.id !== 'edit-redo-alt' && s.id !== 'edit-delete-alt'),
    view: viewShortcuts.filter((s) => s.id !== 'view-zoom-in-alt'),
    file: fileShortcuts,
    help: helpShortcuts,
  };
}

/**
 * Category display names
 */
export const categoryLabels: Record<string, string> = {
  tools: 'Tools',
  edit: 'Edit',
  view: 'View',
  file: 'File',
  help: 'Help',
};
